import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { SCOPE_SUB_SCOPES } from '../../../core/occurrence/occurrence-catalog';
import { SCOPE_LABELS, SUB_SCOPE_LABELS } from '../../../core/occurrence/occurrence.labels';
import { OccurrenceScope, OccurrenceSubScope } from '../../../core/occurrence/occurrence.models';
import { ToastService } from '../../../core/ui/toast.service';
import { PageHeader } from '../../../shared/page-header/page-header';

interface ModuleMailbox {
  readonly scope: OccurrenceScope;
  readonly subScope: OccurrenceSubScope | null;
  address: string;
  purpose: string;
}

/**
 * Caixa de e-mail de cada módulo. Nos sistemas de gestão o endereço é por subâmbito — a ISO 9001
 * (reclamações), a ISO 56001 (ideias) e a NP 4590 têm caixas diferentes; nos restantes âmbitos,
 * uma por âmbito. É daqui que as Comunicações vão tirar o remetente e a caixa a monitorizar.
 */
@Component({
  selector: 'app-module-mailboxes',
  imports: [FormsModule, RouterLink, PageHeader],
  templateUrl: './module-mailboxes.html',
})
export class ModuleMailboxes implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly toasts = inject(ToastService);

  protected readonly rows = signal<ModuleMailbox[]>([]);
  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly scopeLabels = SCOPE_LABELS;
  protected readonly subScopeLabels = SUB_SCOPE_LABELS;

  private base(): string {
    return `/api/t/${this.auth.currentTenant()}/modulos/caixas`;
  }

  ngOnInit(): void {
    this.http.get<ModuleMailbox[]>(this.base()).subscribe({
      next: (saved) => {
        this.rows.set(this.buildRows(saved));
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Não foi possível carregar as caixas.');
        this.loading.set(false);
      },
    });
  }

  /** Uma linha por módulo, com o que já está guardado preenchido. */
  private buildRows(saved: readonly ModuleMailbox[]): ModuleMailbox[] {
    const find = (scope: OccurrenceScope, subScope: OccurrenceSubScope | null) =>
      saved.find((row) => row.scope === scope && (row.subScope ?? null) === subScope);
    const rows: ModuleMailbox[] = [];
    for (const scope of Object.keys(SCOPE_SUB_SCOPES) as OccurrenceScope[]) {
      const subScopes = SCOPE_SUB_SCOPES[scope];
      if (subScopes.length) {
        for (const subScope of subScopes) {
          const existing = find(scope, subScope);
          rows.push({ scope, subScope, address: existing?.address ?? '', purpose: existing?.purpose ?? '' });
        }
      } else {
        const existing = find(scope, null);
        rows.push({ scope, subScope: null, address: existing?.address ?? '', purpose: existing?.purpose ?? '' });
      }
    }
    return rows;
  }

  protected label(row: ModuleMailbox): string {
    return row.subScope
      ? `${this.scopeLabels[row.scope]} · ${this.subScopeLabels[row.subScope]}`
      : this.scopeLabels[row.scope];
  }

  protected save(): void {
    this.error.set(null);
    this.saving.set(true);
    this.http.put<ModuleMailbox[]>(this.base(), { mailboxes: this.rows() }).subscribe({
      next: () => {
        this.saving.set(false);
        this.toasts.success('Caixas guardadas.');
      },
      error: (response) => {
        this.saving.set(false);
        this.error.set(response?.error?.message ?? 'Não foi possível guardar as caixas.');
      },
    });
  }
}
